<template>
  <div class="map-wrapper">
    <!-- Google Map canvas -->
    <div id="map-container" class="map-container"></div>

    <!-- top pill‑style tab switch -->
    <div class="tab-switch">
      <button
        class="tab-btn"
        :class="{ active: activeTab === 'location' }"
        @click="switchTab('location')"
        :disabled="!hasValidLocation"
        style="width:max-content"
      >
        {{ $t ? $t("last_valid_location") : "Last Valid Location" }}
      </button>
      <button
        class="tab-btn"
        :class="{ active: activeTab === 'route' }"
        @click="switchTab('route')"
        :disabled="!hasRoute"
      >
        {{ $t ? $t("route") : "Route" }}
      </button>
    </div>

    <!-- loading overlay (confined to the map) -->
    <loading
      :active.sync="isLoading"
      :is-full-page="false"
      loader="bars"
      class="map-loader"
    />
  </div>
</template>

<script>
import loadGoogleMapsApi from "load-google-maps-api";
import Loading from "vue-loading-overlay";
export default {
  name: "DeviceRoutes2",
  data() {
    return {
      map: null,
      polygonCoords: [],
      polygonShape: null,
      newMap: null,
      newShape_: null,
      drawManager: null,
      polyline: null,
      startMarker: null,
      endMarker: null,
      isLoading: false,
      fullPage: true,
      directionsRenderer: null,
      activeTab: "location", // default – will be corrected in mounted()
      locationMarker: null,
      locationCircle: null,
    };
  },
  methods: {
    initializeMap() {
      const mapZoom = 7;
      const { google } = window;
      const mapOptions = {
        zoom: mapZoom,
        center: { lat: 6.5244, lng: 3.3792 },
        mapTypeControl: true,
        streetViewControl: false,
        mapTypeControlOptions: {
          position: google.maps.ControlPosition.BOTTOM_LEFT,
        },
      };

      const renderOptions = {
        markerOptions: {
          optimized: false,
        },
      };

      const directionsService = new google.maps.DirectionsService();
      this.directionsRenderer = new google.maps.DirectionsRenderer(
        renderOptions
      );
      this.map = new google.maps.Map(
        document.getElementById("map-container"),
        mapOptions
      );
      this.directionsRenderer.setMap(this.map);

      if (!this.mapPoints.start || !this.mapPoints.end) return;

      this.polyline = new google.maps.Polyline({
        path: [],
        geodesic: true,
        strokeColor: "#4285F4",
        strokeOpacity: 0.6,
        strokeWeight: 6,
      });

      this.startMarker = new google.maps.Marker({
        map: this.map,
        title: "Point A",
        label: "A",
      });

      this.endMarker = new google.maps.Marker({
        map: this.map,
        title: "Point B",
        label: "B",
      });

      this.calculateAndDisplayRoute(directionsService);
    },
    resetMap() {
      // 🔑 make absolutely sure any old route is gone
      if (this.directionsRenderer) {
        this.directionsRenderer.set("directions", null); // <-- new line
        this.directionsRenderer.setMap(null);
        this.directionsRenderer = null;
      }

      if (this.polyline) {
        this.polyline.setMap(null);
        this.polyline = null;
      }
      if (this.startMarker) this.startMarker.setMap(null);
      if (this.endMarker) this.endMarker.setMap(null);
      if (this.locationMarker) {
        this.locationMarker.setMap(null);
        this.locationMarker = null;
      }
      if (this.locationCircle) {
        this.locationCircle.setMap(null);
        this.locationCircle = null;
      }
    },
    calculateAndDisplayRoute(directionsService) {
      const { google } = window;
      const { start, end, waypoints } = this.mapPoints;

      this.isLoading = true;

      directionsService
        .route({
          origin: start,
          destination: end,
          waypoints: waypoints,
          optimizeWaypoints: true,
          travelMode: google.maps.TravelMode.DRIVING,
        })
        .then((response) => {
          this.directionsRenderer.setDirections(response);
          const route = response.routes[0].legs[0];
          this.startMarker.setPosition(route.start_location);
          this.endMarker.setPosition(route.end_location);
          this.startMarker.setMap(this.map);
          this.endMarker.setMap(this.map);
          this.isLoading = false;
        })
        .catch((e) => {
          console.error(
            "Directions request failed, drawing polyline as fallback.",
            e
          );
          this.drawPolylineFallback();
        });
    },
    drawPolylineFallback() {
      const routePath = [
        this.mapPoints.start,
        ...this.mapPoints.waypoints.map((waypoint) => waypoint.location), // Extract location
        this.mapPoints.end,
      ];

      this.polyline.setPath(routePath);
      this.polyline.setMap(this.map);

      this.startMarker.setPosition(this.mapPoints.start);
      this.endMarker.setPosition(this.mapPoints.end);

      this.startMarker.setMap(this.map);
      this.endMarker.setMap(this.map);

      const bounds = new google.maps.LatLngBounds();
      routePath.forEach((point) => bounds.extend(point));
      this.map.fitBounds(bounds);

      this.isLoading = false;
    },
    switchTab(tab) {
      if (this.activeTab === tab) return;
      this.activeTab = tab;
      this.renderTab();
    },
    renderTab() {
      // Clear previous drawings
      this.resetMap();

      if (this.activeTab === "location") {
        this.showLastValidLocation();
      } else {
        this.initializeMap(); // existing route logic
      }
    },

    /**
     * Ensure base map exists (creates it once and reuses thereafter)
     */
    createBaseMap(center = { lat: 6.5244, lng: 3.3792 }, zoom = 7) {
      const { google } = window;
      if (this.map) {
        // just panning/zooming instead of rebuilding
        this.map.setCenter(center);
        this.map.setZoom(zoom);
        return;
      }

      this.map = new google.maps.Map(document.getElementById("map-container"), {
        zoom,
        center,
        mapTypeControl: true,
        streetViewControl: false,
        mapTypeControlOptions: {
          position: google.maps.ControlPosition.BOTTOM_LEFT,
        },
      });
    },

    showLastValidLocation() {
      if (!this.hasValidLocation) return;

      const { google } = window;
      const position = {
        lat: parseFloat(this.lastValidLocation.latitude),
        lng: parseFloat(this.lastValidLocation.longitude),
      };

      this.isLoading = true;
      this.createBaseMap(position, 16);

      // focal marker
      this.locationMarker = new google.maps.Marker({
        position,
        map: this.map,
        title: "Last Valid Location",
        icon: {
          url: "http://maps.google.com/mapfiles/ms/icons/red-dot.png",
        },
      });

      // soft‑grey radius, purely cosmetic
      this.locationCircle = new google.maps.Circle({
        center: position,
        radius: 200, // metres
        fillColor: "#9EA7B8",
        fillOpacity: 0.3,
        strokeOpacity: 0,
        map: this.map,
      });

      this.isLoading = false;
    },
  },
  mounted() {
    loadGoogleMapsApi({
      key: process.env.VUE_APP_GOOGLE_API_KEY,
      libraries: ["places", "drawing", "geometry"],
    }).then(() => {
      // pick safest initial tab
      this.activeTab = this.hasValidLocation ? "location" : "route";
      this.renderTab();
    });
  },
  props: {
    stats: {
      type: Array,
      default: () => [],
    },
    mapPoints: Object,
    lastValidLocation: {
      type: Object || null,
      default: () => null,
    },
  },
  components: {
    Loading,
  },
  computed: {
    hasValidLocation() {
      return (
        this.lastValidLocation &&
        isFinite(parseFloat(this.lastValidLocation.latitude)) &&
        isFinite(parseFloat(this.lastValidLocation.longitude))
      );
    },
    hasRoute() {
      return this.mapPoints && this.mapPoints.start && this.mapPoints.end;
    },
  },
  watch: {
    stats: {
      handler: function () {
        // no need to refresh on stats changes for new tab logic
      },
      deep: true,
    },
    mapPoints: {
      handler() {
        if (this.activeTab === "route") {
          this.renderTab();
        }
      },
      deep: true,
    },
    lastValidLocation: {
      handler() {
        if (this.activeTab === "location") {
          this.renderTab();
        }
      },
      deep: true,
    },
  },
};
</script>

<style scoped>
.map-container {
  height: 100%;
}
.tab-switch {
  position: absolute;
  top: 10px;
  left: 50%;
  transform: translateX(-50%);
  background: #ffffff;
  border-radius: 32px;
  box-shadow: 0 0 4px rgba(0, 0, 0, 0.15);
  display: flex;
  overflow: hidden;
}

.tab-btn {
  border: none;
  background: transparent;
  padding: 6px 24px;
  font-weight: 600;
  font-size: 14px;
  color: #213e7a;
  cursor: pointer;
  transition: background 0.2s ease;
}

.tab-btn.active {
  background: #213e7a;
  color: #ffffff;
}

.tab-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.map-wrapper {
  position: relative;
  height: 100%;
  width: 100%;
}

/* ensure the Google map fills its wrapper */
.map-container {
  height: 100%;
  width: 100%;
}

/* keep the pill tabs on top of Google overlays */
/* .tab-switch {
  z-index: 5;
} */

/* optional: make loader sit above map tiles but below tabs */
.map-loader {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  /* z-index: 4; */
}
</style>
